import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FastField } from 'formik';

import { Block as LabelBlock } from '../Label';
import FieldError from './FieldError';
import {
  Input as BoneyardInput,
  MaskedInput,
  VisuallyHidden,
} from '../Boneyard';
import getNamespacedFieldName from './getNamespacedFieldName';
import getFormPartAutomationHook from './getFormPartAutomationHook';
import getFormikFieldProps from './getFormikFieldProps';
import useFastFieldShouldRenderFn from './useFastFieldShouldRenderFn';

const propTypes = {
  //  //to be true when there should be no visible label;

  ariaLabelOnly: PropTypes.bool,
  // Taken into consideration only when ariaLabelOnly is set to true.
  // If set to true, it will visually maintain the empty height of the label
  // on top of the field.
  labelTransparent: PropTypes.bool,
  name: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  namespace: PropTypes.string,
  automationHook: PropTypes.string.isRequired,
  resetOnUnmount: PropTypes.bool,
  mask: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  children: PropTypes.node,
  forceErrorStyles: PropTypes.bool,
  fieldLevelValidation: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyPress: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  showOwnErrorMessage: PropTypes.bool,
  type: PropTypes.string,
  infoButton: PropTypes.node,
  autoComplete: PropTypes.string,
  isDisabled: PropTypes.bool,

  // ... plus other Boneyard Input fields
};

const VisuallyTransparent = styled.div`
  visibility: hidden;
`;

const Label = styled(props => (
  <div className={props.className}>{props.children}</div>
))`
  display: flex;
  align-items: center;
  font-weight: 500;
`;

export const InputField = ({
  ariaLabelOnly,
  labelTransparent,
  children,
  namespace,
  label,
  isRequired,
  name,
  automationHook,
  forceErrorStyles,
  fieldLevelValidation,
  onBlur,
  onChange,
  onKeyPress,
  onKeyDown,
  onKeyUp,
  resetOnUnmount = false,
  showOwnErrorMessage = true,
  infoButton = null,
  autoComplete,
  isDisabled,
  ...props
}) => {
  const fieldName = getNamespacedFieldName(name, namespace);
  const fieldAutomationHook = getFormPartAutomationHook(
    automationHook,
    name,
    namespace
  );
  const InputComponent = !props.mask ? BoneyardInput : MaskedInput;
  const HidingComponent = labelTransparent
    ? VisuallyTransparent
    : VisuallyHidden;

  const shouldUpdate = useFastFieldShouldRenderFn(['validate']);
  return (
    <>
      {label && !ariaLabelOnly ? (
        <LabelBlock
          htmlFor={fieldName}
          isRequired={isRequired}
          automationHook={fieldAutomationHook}
        >
          <Label>
            {label}
            {infoButton}
          </Label>
        </LabelBlock>
      ) : (
        <HidingComponent>
          <LabelBlock
            htmlFor={fieldName}
            isRequired={isRequired}
            automationHook={fieldAutomationHook}
          >
            <Label>
              {label}
              {infoButton}
            </Label>
          </LabelBlock>
        </HidingComponent>
      )}
      <FastField
        name={fieldName}
        shouldUpdate={shouldUpdate}
        validate={fieldLevelValidation}
      >
        {({ field, form, meta }) => {
          const fieldWithCustomHandlers = getFormikFieldProps({
            fieldName,
            field,
            meta,
            forceErrorStyles,
            onChange,
            onBlur,
            onKeyPress,
            onKeyDown,
            onKeyUp,
            fieldLevelValidation,
          });

          return (
            <>
              <InputComponent
                {...props}
                autoComplete={autoComplete}
                type={props.type || 'text'}
                id={fieldName}
                automationHook={fieldAutomationHook}
                ariaDescribedBy={`${fieldName}_error`}
                {...fieldWithCustomHandlers}
                disabled={isDisabled}
              >
                {children}
              </InputComponent>
              {showOwnErrorMessage ? (
                <FieldError fieldName={fieldName} label={label} />
              ) : null}
            </>
          );
        }}
      </FastField>
    </>
  );
};
InputField.propTypes = propTypes;
InputField.displayName = 'InputField';

export default React.memo(InputField);
